// frontend/app/components/discoveryJourneys/JourneyMasteryMap.js
//
// The Mastery Map from the Saras Learning Loop document: every concept the
// journey's loops declare, with where the learner stands on each.
//
// One component, two audiences, off a single `mode` prop:
//   * student — my band per concept, and what Saras says about moving up;
//   * teacher — the class mean plus how the class is spread across the four
//     bands, which is the question a teacher actually has.
//
// Two rules the whole file exists to keep:
//   * `mastery === null` is NOT zero. A concept nobody has been checked on is
//     unknown, and a 0% bar is a lie a teacher would act on. Null renders as
//     "Not enough evidence yet" with no fill at all.
//   * every bar carries its `n`. A band from one answer and a band from twenty
//     look identical otherwise, and the smoothed score is specifically designed
//     so that thin evidence reads as thin.
//
// Hand-rolled Views throughout — no chart library is installed, and a mastery
// bar is a rectangle.

import React from "react";
import { ActivityIndicator, StyleSheet, Text, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { getLessonColors, getMasteryBandMeta, tint } from "../lessonPlans/lessonPlanTheme";
import { fill } from "./journeyProgress";
import {
  BAND_ORDER,
  bandKeyFor,
  distributionOf,
  masteryOf,
  sampleOf,
  usableConcepts,
} from "./journeyMastery";

const EMPTY_DISTRIBUTION = { counts: [], total: 0 };

/** The four bands as a colour key, so a segment's colour is readable. */
const BandLegend = ({ colors, t }) => (
  <View testID="journey-mastery-legend" style={styles.legend}>
    {BAND_ORDER.map((key) => {
      const meta = getMasteryBandMeta(key);
      return (
        <View key={key} style={styles.legendItem}>
          <View style={[styles.legendSwatch, { backgroundColor: meta.accent }]} />
          <Text style={[styles.legendText, { color: colors.textMuted }]}>
            {t(meta.label)}
          </Text>
        </View>
      );
    })}
  </View>
);

/** The class spread across the four bands, as one segmented bar plus counts. */
const Distribution = ({ concept, counts, total, colors, t }) => (
  <View testID={`journey-mastery-distribution-${concept.concept_key}`}>
    <View style={[styles.stack, { backgroundColor: colors.subtleBg, borderColor: colors.border }]}>
      {BAND_ORDER.map((key, index) => {
        const count = counts[index];
        if (!count) return null;
        const meta = getMasteryBandMeta(key);
        return (
          <View
            key={key}
            testID={`journey-mastery-segment-${concept.concept_key}-${key}`}
            style={[
              styles.segment,
              { width: `${(count / total) * 100}%`, backgroundColor: meta.accent },
            ]}
          />
        );
      })}
    </View>
    <View style={styles.counts}>
      {BAND_ORDER.map((key, index) => {
        const count = counts[index];
        if (!count) return null;
        const meta = getMasteryBandMeta(key);
        return (
          <View key={key} style={styles.countItem}>
            <View style={[styles.legendSwatch, { backgroundColor: meta.accent }]} />
            <Text style={[styles.countText, { color: colors.textSecondary }]}>
              {fill(t("{label} {count}"), { label: t(meta.label), count })}
            </Text>
          </View>
        );
      })}
    </View>
  </View>
);

const ConceptRow = ({ concept, mode, colors, isDarkMode, divided, t }) => {
  const mastery = masteryOf(concept);
  const bandKey = concept?.band || bandKeyFor(mastery);
  const meta = getMasteryBandMeta(bandKey);
  const sample = sampleOf(concept, mode);
  const { counts, total } =
    mode === "teacher" ? distributionOf(concept) : EMPTY_DISTRIBUTION;

  const hasScore = mastery !== null;
  const hasEvidence = hasScore || total > 0;
  const pct = hasScore ? Math.round(mastery * 100) : null;
  const loopTitle = String(concept?.loop_title || "").trim();

  const sampleText =
    mode === "teacher"
      ? fill(t(sample === 1 ? "n = {n} student" : "n = {n} students"), { n: sample })
      : fill(t(sample === 1 ? "n = {n} answer" : "n = {n} answers"), { n: sample });

  const accessibilityLabel = hasScore
    ? `${concept.label}: ${t(meta.label)}, ${pct}%, ${sampleText}`
    : `${concept.label}: ${t("Not enough evidence yet")}`;

  return (
    <View
      testID={`journey-mastery-concept-${concept.concept_key}`}
      accessibilityLabel={accessibilityLabel}
      style={[
        styles.row,
        divided ? { borderTopWidth: 1, borderTopColor: colors.border } : null,
      ]}
    >
      <View style={styles.rowTop}>
        <View style={styles.rowLabel}>
          <Text style={[styles.conceptLabel, { color: colors.textPrimary }]} numberOfLines={2}>
            {concept.label || concept.concept_key}
          </Text>
          {loopTitle ? (
            <Text style={[styles.conceptLoop, { color: colors.textMuted }]} numberOfLines={1}>
              {loopTitle}
            </Text>
          ) : null}
        </View>
        {hasScore ? (
          <>
            <Text style={[styles.pct, { color: meta.accent }]}>{`${pct}%`}</Text>
            <View
              testID={`journey-mastery-band-${concept.concept_key}`}
              style={[
                styles.bandPill,
                { backgroundColor: tint(meta.accent, isDarkMode, 1.8), borderColor: meta.accent },
              ]}
            >
              <Text style={[styles.bandText, { color: meta.accent }]}>{t(meta.label)}</Text>
            </View>
          </>
        ) : null}
      </View>

      {hasScore ? (
        <View
          testID={`journey-mastery-track-${concept.concept_key}`}
          style={[styles.track, { backgroundColor: colors.subtleBg, borderColor: colors.border }]}
        >
          <View
            testID={`journey-mastery-fill-${concept.concept_key}`}
            style={[
              styles.fill,
              {
                // A measured zero keeps a 4px stub so it can never be confused
                // with the dashed empty track of a concept with no evidence.
                width: pct > 0 ? `${pct}%` : 4,
                backgroundColor: meta.accent,
              },
            ]}
          />
        </View>
      ) : (
        <View
          testID={`journey-mastery-empty-${concept.concept_key}`}
          style={[styles.trackEmpty, { borderColor: colors.border }]}
        />
      )}

      {mode === "teacher" && total > 0 ? (
        <Distribution
          concept={concept}
          counts={counts}
          total={total}
          colors={colors}
          t={t}
        />
      ) : null}

      <View style={styles.evidence}>
        {hasEvidence ? (
          <Text testID={`journey-mastery-n-${concept.concept_key}`} style={[styles.evidenceText, { color: colors.textMuted }]}>
            {sampleText}
          </Text>
        ) : (
          <Text
            testID={`journey-mastery-unknown-${concept.concept_key}`}
            style={[styles.evidenceText, { color: colors.textMuted }]}
          >
            {t("Not enough evidence yet")}
          </Text>
        )}
        {mode === "student" && concept?.transition_message ? (
          <Text style={[styles.transition, { color: colors.textSecondary }]}>
            {concept.transition_message}
          </Text>
        ) : null}
      </View>
    </View>
  );
};

const JourneyMasteryMap = ({
  concepts = [],
  // "student" — my bands. "teacher" — the class mean and its spread.
  mode = "student",
  title = "Mastery Map",
  subtitle = null,
  loading = false,
  error = null,
  isDarkMode = false,
  style,
  t = (s) => s,
}) => {
  const colors = getLessonColors(isDarkMode);
  const isTeacher = mode === "teacher";
  const list = usableConcepts(concepts);

  const caption =
    subtitle ||
    (isTeacher
      ? "Where the class stands on every concept in this journey."
      : "Where you stand on every concept in this journey.");

  const body = loading ? (
    <View style={styles.stateBox}>
      <ActivityIndicator size="small" color={colors.accentPrimary} />
      <Text style={[styles.stateText, { color: colors.textMuted }]}>
        {t("Loading the mastery map…")}
      </Text>
    </View>
  ) : error ? (
    <View style={[styles.stateBox, styles.stateBoxBordered, { borderColor: colors.border }]}>
      <MaterialCommunityIcons name="alert-outline" size={16} color={colors.textMuted} />
      <Text style={[styles.stateText, { color: colors.textMuted }]}>
        {t("The mastery map could not be loaded.")}
      </Text>
    </View>
  ) : list.length === 0 ? (
    <View
      testID="journey-mastery-empty"
      style={[styles.stateBox, styles.stateBoxBordered, { borderColor: colors.border }]}
    >
      <MaterialCommunityIcons name="chart-timeline-variant" size={16} color={colors.textMuted} />
      <Text style={[styles.stateText, { color: colors.textMuted }]}>
        {t("No concepts have been declared by this journey's loops yet.")}
      </Text>
    </View>
  ) : (
    <View testID="journey-mastery-rows">
      {list.map((concept, index) => (
        <ConceptRow
          key={concept.concept_key}
          concept={concept}
          mode={mode}
          colors={colors}
          isDarkMode={isDarkMode}
          divided={index > 0}
          t={t}
        />
      ))}
    </View>
  );

  return (
    <View
      testID="journey-mastery-map"
      style={[
        styles.panel,
        { backgroundColor: colors.panelBg, borderColor: colors.border },
        style,
      ]}
    >
      <View style={styles.header}>
        <View style={[styles.headerIcon, { backgroundColor: tint(colors.accentPrimary, isDarkMode, 2.4) }]}>
          <MaterialCommunityIcons
            name="chart-timeline-variant-shimmer"
            size={16}
            color={colors.accentPrimary}
          />
        </View>
        <View style={styles.headerText}>
          <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>{t(title)}</Text>
          <Text style={[styles.headerSubtitle, { color: colors.textMuted }]}>{t(caption)}</Text>
        </View>
      </View>

      {isTeacher && list.length > 0 && !loading && !error ? (
        <BandLegend colors={colors} t={t} />
      ) : null}

      {body}
    </View>
  );
};

const styles = StyleSheet.create({
  panel: { borderRadius: 16, borderWidth: 1, padding: 16 },
  header: { flexDirection: "row", alignItems: "center", gap: 10, marginBottom: 12 },
  headerIcon: {
    width: 30,
    height: 30,
    borderRadius: 9,
    alignItems: "center",
    justifyContent: "center",
  },
  headerText: { flex: 1, minWidth: 0 },
  headerTitle: { fontSize: 15, fontWeight: "800" },
  headerSubtitle: { fontSize: 12, marginTop: 1 },

  legend: { flexDirection: "row", flexWrap: "wrap", gap: 12, marginBottom: 12 },
  legendItem: { flexDirection: "row", alignItems: "center", gap: 5 },
  legendSwatch: { width: 9, height: 9, borderRadius: 3 },
  legendText: { fontSize: 11, fontWeight: "600", marginLeft: 5 },

  stateBox: { flexDirection: "row", alignItems: "center", paddingVertical: 14, gap: 8 },
  stateBoxBordered: { borderWidth: 1, borderRadius: 12, paddingHorizontal: 12 },
  stateText: { fontSize: 13, marginLeft: 8 },

  row: { paddingVertical: 12 },
  rowTop: { flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 8 },
  rowLabel: { flex: 1, minWidth: 0 },
  conceptLabel: { fontSize: 14, fontWeight: "700", lineHeight: 19 },
  conceptLoop: { fontSize: 11, marginTop: 2 },
  pct: { fontSize: 14, fontWeight: "800" },
  bandPill: { borderRadius: 999, borderWidth: 1, paddingHorizontal: 8, paddingVertical: 2 },
  bandText: { fontSize: 10, fontWeight: "800", letterSpacing: 0.3 },

  track: { height: 10, borderRadius: 999, borderWidth: 1, overflow: "hidden" },
  fill: { height: "100%", borderRadius: 999 },
  // Dashed and unfilled: visibly a question, not a score of zero.
  trackEmpty: { height: 10, borderRadius: 999, borderWidth: 1, borderStyle: "dashed" },

  stack: {
    flexDirection: "row",
    height: 10,
    borderRadius: 999,
    borderWidth: 1,
    overflow: "hidden",
    marginTop: 8,
  },
  segment: { height: "100%" },
  counts: { flexDirection: "row", flexWrap: "wrap", gap: 10, marginTop: 6 },
  countItem: { flexDirection: "row", alignItems: "center", gap: 4 },
  countText: { fontSize: 11, fontWeight: "600", marginLeft: 4 },

  evidence: { marginTop: 6 },
  evidenceText: { fontSize: 11, fontWeight: "600" },
  transition: { fontSize: 12, lineHeight: 17, marginTop: 4 },
});

export default JourneyMasteryMap;
